<template>
    <div class="col-md-12">
        <div class="d-flex justify-content-between">
            <div class="h2">SMS Template</div>
            <div>
                <a :href="backUrl">
                    <button class="btn btn-primary">Back</button>
                </a>
            </div>
        </div>
        <div class="flash-message">
            <FlashMessage :position="'top'"></FlashMessage>
        </div>
        <div class="row">
            <div class="col-md-4">
                <div class="form-group">
                    <div>
                        <label for="title" class="mb-0 font-weight-bold">Title</label>
                        <input type="text" id="title" class="form-control" v-model="smsTemplate.title" />
                    </div>
                    <div class="help-block with-errors text-danger" v-if="this.errors.title">{{ this.errors.title }}</div>

                    <div class="mt-4">
                        <label for="active" class="mb-0 font-weight-bold mr-2">Activate</label>
                        <input type="checkbox" id="active" v-model="smsTemplate.active" />
                    </div>
                </div>
                <div class="form-group mt-5">
                    <div><strong>Send when</strong></div>
                    <div v-for="(option, optionIndex) in sendWhenOptions">
                        <label class="newInput" :for="`sendWhen${optionIndex}`">
                            {{ option.name }}
                            <input type="checkbox"
                                   :value="optionIndex"
                                   :id="`sendWhen${optionIndex}`"
                                   v-model="sendWhenOptions[optionIndex].selected"
                            >
                            <span class="checkmark"></span>
                        </label>

                    </div>
                </div>
            </div>
            <div class="col-md-2">
                <div>
                    <label for="time" class="mb-0 font-weight-bold">Time</label>
                    <div class="d-flex align-items-center bg-white border rounded-lg div-focus border-2">
                        <vue-timepicker
                            manual-input
                            placeholder="Time"
                            v-model="smsTemplate.time"
                            input-class="border-0 py-0 shadow-none form-input">
                        </vue-timepicker>
                    </div>
                    <div class="help-block with-errors text-danger" v-if="this.errors.time">{{ this.errors.time }}</div>
                </div>
                <div class="mt-4">
                    <label for="send-to" class="mb-0 font-weight-bold">Send to</label>
                    <v-select
                        :options="sendToOptions"
                        v-model="selectedSendTo"
                        :clearable="false"
                    ></v-select>
                    <div class="help-block with-errors text-danger" v-if="this.errors.send_to">{{ this.errors.send_to }}</div>
                </div>
            </div>
            <div class="col-md-6">
                <div class="form-group">
                    <div>
                        <label for="text" class="mb-0 font-weight-bold">Text</label>
                        <textarea type="text" id="text" class="form-control" rows="5" v-model="smsTemplate.text"></textarea>
                        <div><strong>Available shortcuts:</strong></div>
                        <div v-for="(label, key) in shortcutsLabels">
                            <strong>{{ key }}</strong> - {{ label }}
                        </div>
                    </div>
                    <div class="help-block with-errors text-danger" v-if="this.errors.text">{{ this.errors.text }}</div>
                </div>
            </div>
        </div>

        <div class="h4">Manage conditions</div>
        <div class="help-block with-errors text-danger" v-if="this.errors.conditions">{{ this.errors.conditions }}</div>
        <div v-for="(conditionBlock, blockId) in smsTemplate.conditions">
            <condition-block :key="blockId" :block-id="blockId" :condition-block="conditionBlock"></condition-block>
        </div>

        <div class="row mt-4">
            <div class="col-md-12 d-flex">
                <button class="btn btn-primary mr-4" @click="save">
                    Save
                </button>
                <button class="btn btn-primary" @click="onClickSendSms" v-if="smsTemplate.id">
                    Send SMS
                </button>
            </div>
        </div>

        <sms-result-modal ref="smsResultModal"></sms-result-modal>

        <loading :active.sync="isLoading"
                 :can-cancel="false"
                 :is-full-page="true">
        </loading>
    </div>

</template>

<script>
import VueTimepicker from 'vue2-timepicker';
import 'vue2-timepicker/dist/VueTimepicker.css';
import Loading from "vue-loading-overlay";
import 'vue-loading-overlay/dist/vue-loading.css';
import 'vue-select/dist/vue-select.css';
import ConditionBlock from "./reusable/ConditionBlock.vue";
import SmsResultModal from "./reusable/SmsResultModal.vue";

export default {
    name: "SmsTemplate",
    components: {ConditionBlock, Loading, VueTimepicker, SmsResultModal},
    props: {
        backUrl: {type: String, required: true},
        smsTemplateProp: {
            type: Object, required: false, default() {
                return {
                    id: '',
                    title: '',
                    time: '',
                    active: false,
                    text: '',
                    send_to: '',
                    send_when: [],
                    conditions: { // If necessary it'll be easy to add a possibility to manage multiple blocks
                        block_1: {
                            conditions: {
                                cond_1: {field: '', value: '', operator: ''}
                            },
                            operator: ''
                        }
                    },
                }
            }
        },
        conditionTypes: {type: Object, required: true},
        conditionFields: {type: Array, required: true},
        shortcutsLabels: {type: Object, required: true},
        sendToOptions: {type: Array, required: true},
        sendWhenOptionsProp: {type: Object, required: true},
    },
    data() {
        return {
            isLoading: false,
            smsTemplate: JSON.parse(JSON.stringify(this.smsTemplateProp)),
            errors: {},
            smsModalType: 'prepare',
            selectedSendTo: this.sendToOptions[this.smsTemplateProp.send_to],
            sendWhenOptions: JSON.parse(JSON.stringify(this.sendWhenOptionsProp)),
        }
    },
    methods: {
        addCondition(blockId, operator) {
            let conditionKeys = Object.keys(this.smsTemplate.conditions[blockId].conditions);
            let lastCond = conditionKeys[conditionKeys.length - 1];
            let i = lastCond.replaceAll(/\D/gi, '');
            i = parseInt(i) + 1;
            i = 'cond_' + i;
            this.$set(this.smsTemplate.conditions[blockId].conditions[lastCond], 'operator', operator);
            this.$set(this.smsTemplate.conditions[blockId].conditions, i, {field: '', value: '', operator: ''});
        },
        setCondition(blockId, conditionId, value) {
            this.$set(this.smsTemplate.conditions[blockId].conditions, conditionId, value);
        },
        removeCondition(blockId, conditionId) {
            if (Object.keys(this.smsTemplate.conditions[blockId].conditions).length === 1) {
                return;
            }
            this.$delete(this.smsTemplate.conditions[blockId].conditions, conditionId);
        },
        save() {
            this.isLoading = true;
            this.errors = {};
            let method, url;
            if (this.smsTemplate.id) {
                method = 'put';
                url = `/sms/templates/${this.smsTemplate.id}/update`;
            } else {
                method = 'post';
                url = '/sms/templates/store';
            }
            if (this.sendToOptions.includes(this.selectedSendTo)) {
                const callback = (element) => element === this.selectedSendTo;
                this.$set(this.smsTemplate, 'send_to', this.sendToOptions.findIndex(callback))
            }

            this.smsTemplate.send_when = [];
            for (let i in this.sendWhenOptions) {
                if (this.sendWhenOptions[i].selected) {
                    this.smsTemplate.send_when.push(i);
                }
            }

            axios[method](url, this.smsTemplate)
                .then((response) => {
                    this.flashMessage.success({
                        title: '',
                        message: `Saved successfully!`,
                        time: 2000,
                        blockClass: 'flash-message'
                    });
                    if (response.data.id) {
                        window.location.href = `/sms`
                    }
                }).catch((err) => {
                    this.flashError(err, `Sms template wasn't saved`);
                }).finally(() => {
                    this.isLoading = false;
                });
        },
        onClickSendSms() {
            this.errors = {};
            let method, url;
            method = 'put';
            url = `/sms/templates/${this.smsTemplate.id}/update`;

            axios[method](url, this.smsTemplate)
                .then((response) => {
                    this.$refs.smsResultModal.prepareDataForSms(this.smsTemplate);
                }).catch((err) => {
                this.flashError(err, `Sms template wasn't saved`);
            });
        },
        flashError(error, defaultMassage = 'Error') {
            console.error(error.response);
            this.flashMessage.error({
                title: '',
                message: error.response.data.message || error.response.data ||  defaultMassage,
                time: 4000,
                blockClass: 'flash-message error'
            });
            if (error.response.data.errors) {
                if (error.response.data.errors.title) {
                    this.$set(this.errors, 'title', error.response.data.errors.title[0])
                }
                if (error.response.data.errors.time) {
                    this.$set(this.errors, 'time', error.response.data.errors.time[0])
                }
                if (error.response.data.errors.text) {
                    this.$set(this.errors, 'text', error.response.data.errors.text[0])
                }
                if (error.response.data.errors.conditions) {
                    this.$set(this.errors, 'conditions', error.response.data.errors.conditions[0])
                }
                if (error.response.data.errors.send_to) {
                    this.$set(this.errors, 'send_to', error.response.data.errors.send_to[0])
                }
            }
        },

    }

}
</script>

<style scoped>


</style>
